/**
 * iPad: Field Lube Report (Rapid Lube). Items and lubricants come from the profile lists in
 * the job payload, so it works offline. "Lube Report Done" saves and marks it complete; the
 * server checks it is really complete (D21).
 */
import { useState } from 'react';
import { OTHER_LUBRICANT } from '@wo/shared';
import { enqueueItem } from './queue';
import type { FormLists, MechForms, MechJob } from './types';
import { Big, BigField, Label, NoteBox, Sheet, bigInput } from './ui';

type SetForms = (fn: (f: MechForms) => MechForms) => void;

export function LubeSheet({ job, lists, userId, setForms, onClose }: { job: MechJob; lists: FormLists; userId: number; setForms: SetForms; onClose: () => void }) {
  const l = job.forms.lube;
  const [h, setH] = useState({
    customerEquipmentNo: l?.header.customerEquipmentNo ?? job.machine.equipmentNo ?? '',
    equipHours: l?.header.hours ?? '',
    typeOfService: l?.header.typeOfService ?? '',
    nextService: l?.header.nextService ?? '',
  });
  const [remarks, setRemarks] = useState(l?.remarks ?? '');
  const [items, setItems] = useState(lists.lubeItems.map((item) => ({ item, checked: l?.items.find((i) => i.item === item)?.checked ?? false, changed: l?.items.find((i) => i.item === item)?.changed ?? false })));
  const [lubes, setLubes] = useState([
    ...lists.lubricants.map((lubricant) => ({ lubricant, otherName: '', gallons: l?.lubricants.find((x) => x.lubricant === lubricant)?.gallons ?? '' })),
    ...(l?.lubricants.filter((x) => x.lubricant === OTHER_LUBRICANT).map((x) => ({ ...x, gallons: x.gallons ?? '' })) ?? []),
  ]);
  const canEdit = job.status === 'ASSIGNED' || job.status === 'IN_PROGRESS' || job.status === 'ON_HOLD';
  const hoursOk = h.equipHours === '' || /^\d{1,9}(\.\d)?$/.test(h.equipHours);
  const gallonsOk = lubes.every((x) => x.gallons === '' || /^\d{1,8}(\.\d{1,2})?$/.test(x.gallons));
  const complete = h.equipHours !== '' && h.typeOfService.trim() !== '' && items.some((i) => i.checked || i.changed);

  const save = async (done: boolean) => {
    // Send filled-in rows, and rows that had gallons before (cleared = 0; rows are never deleted).
    const had = (x: { lubricant: string; otherName: string }) => l?.lubricants.some((y) => y.lubricant === x.lubricant && y.otherName === x.otherName.trim() && y.gallons);
    const lubricants = lubes.filter((x) => (x.gallons !== '' || had(x)) && (x.lubricant !== OTHER_LUBRICANT || x.otherName.trim())).map((x) => ({ lubricant: x.lubricant, otherName: x.otherName.trim(), gallons: x.gallons }));
    await enqueueItem(userId, `Lube report #${job.number}`, { type: 'lube_save', workOrderId: job.id, ...h, remarks, items, lubricants });
    if (done) await enqueueItem(userId, `Lube report done #${job.number}`, { type: 'lube_submit', workOrderId: job.id });
    setForms((f) => ({
      ...f,
      lube: {
        header: { ...(f.lube?.header ?? { customerName: job.customer.name, make: job.machine.make, model: job.machine.model, serialNumber: job.machine.serialNumber, machineLocation: job.machineLocation }), customerEquipmentNo: h.customerEquipmentNo, hours: h.equipHours || null, typeOfService: h.typeOfService || null, nextService: h.nextService || null },
        items,
        lubricants: lubes,
        remarks,
        submittedAt: done ? new Date().toISOString() : (f.lube?.submittedAt ?? null),
        missing: done ? [] : (f.lube?.missing ?? []),
      },
    }));
    onClose();
  };

  const toggle = (i: number, k: 'checked' | 'changed') => setItems(items.map((x, j) => (j === i ? { ...x, [k]: !x[k] } : x)));

  return (
    <Sheet title={`Field Lube Report${lists.lubeBrand ? ` (${lists.lubeBrand})` : ''} — #${job.number}`} onClose={onClose}>
      {l?.submittedAt && <p className="mb-2 rounded-lg bg-green-100 p-3 text-lg text-green-900">Marked done. You can still fix it until the job goes for review.</p>}
      <p className="text-lg">{job.customer.name} · {job.machine.make} {job.machine.model} · S/N {job.machine.serialNumber}</p>
      <div className="grid grid-cols-2 gap-x-3">
        <BigField label="Customer equipment no." value={h.customerEquipmentNo} onChange={(e) => setH({ ...h, customerEquipmentNo: e.target.value })} disabled={!canEdit} />
        <BigField label="Hours" inputMode="decimal" value={h.equipHours} onChange={(e) => setH({ ...h, equipHours: e.target.value.replace(/[^\d.]/g, '') })} disabled={!canEdit} />
        <BigField label="Type of service" value={h.typeOfService} onChange={(e) => setH({ ...h, typeOfService: e.target.value })} disabled={!canEdit} placeholder="e.g. 500 hour" />
        <BigField label="Next service" value={h.nextService} onChange={(e) => setH({ ...h, nextService: e.target.value })} disabled={!canEdit} />
      </div>

      <Label>Check / Change</Label>
      <ul className="divide-y rounded-xl border-2 border-slate-200">
        {items.map((it, i) => (
          <li key={it.item} className="flex items-center gap-2 p-2">
            <span className="flex-1 text-lg">{it.item}</span>
            <Big tone={it.checked ? 'go' : 'light'} className="min-h-[56px] min-w-[100px] border-2 border-slate-300 px-2" disabled={!canEdit} onClick={() => toggle(i, 'checked')}>{it.checked ? '✓ Check' : 'Check'}</Big>
            <Big tone={it.changed ? 'go' : 'light'} className="min-h-[56px] min-w-[110px] border-2 border-slate-300 px-2" disabled={!canEdit} onClick={() => toggle(i, 'changed')}>{it.changed ? '✓ Change' : 'Change'}</Big>
          </li>
        ))}
      </ul>

      <Label>Lubricants — gallons</Label>
      <div className="grid grid-cols-2 gap-2">
        {lubes.map((x, i) => (
          <div key={`${x.lubricant}:${i}`} className="flex items-center gap-2">
            {x.lubricant === OTHER_LUBRICANT ? (
              <input className={`${bigInput} flex-1`} placeholder="Other (name)" value={x.otherName} disabled={!canEdit} onChange={(e) => setLubes(lubes.map((y, j) => (j === i ? { ...y, otherName: e.target.value } : y)))} />
            ) : (
              <span className="flex-1 text-lg font-semibold">{x.lubricant}</span>
            )}
            <input className={`${bigInput} w-28`} inputMode="decimal" aria-label={`${x.lubricant} gallons`} value={x.gallons} disabled={!canEdit} onChange={(e) => setLubes(lubes.map((y, j) => (j === i ? { ...y, gallons: e.target.value.replace(/[^\d.]/g, '') } : y)))} />
          </div>
        ))}
      </div>
      {canEdit && <Big tone="light" className="mt-2 border-2 border-slate-300" onClick={() => setLubes([...lubes, { lubricant: OTHER_LUBRICANT, otherName: '', gallons: '' }])}>+ Other lubricant</Big>}

      <Label>Remarks</Label>
      <NoteBox value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!canEdit} placeholder="Anything the customer should know" />

      {canEdit && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Big disabled={!hoursOk || !gallonsOk} onClick={() => save(false)}>Save</Big>
          <Big tone="go" disabled={!hoursOk || !gallonsOk || !complete} onClick={() => save(true)}>Lube Report Done</Big>
        </div>
      )}
      {!complete && canEdit && <p className="mt-2 text-base text-slate-600">To finish: hours, type of service, and at least one item marked Check or Change.</p>}
      {(l?.missing.length ?? 0) > 0 && l?.submittedAt === null && <p className="mt-2 text-base text-red-800">Still needed: {l!.missing.join('; ')}</p>}
    </Sheet>
  );
}
